import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation, type UseTranslationResult } from '@govard/i18n-core/react';
import {
  EntityRelatedList,
  type EntityRelatedRow,
} from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { formatDateTime, formatMoney } from '../../../shared/format/entity-detail.js';
import { formatPersonName } from '../../../shared/format/index.js';
import { DealFormModal } from '../../deals/components/DealFormModal.js';
import type { DealListItem } from '../../deals/api/deals.client.js';

const STAGE_ORDER = ['New', 'Contacted', 'Negotiation', 'Won', 'Lost'];

export function isDealOpen(deal: DealListItem): boolean {
  return deal.stage !== 'Won' && deal.stage !== 'Lost';
}

/** Просрочка — только у открытой сделки, как в карточке сделки и в истории сервера. */
export function isDealNextContactOverdue(deal: DealListItem, now = Date.now()): boolean {
  return (
    isDealOpen(deal) && Boolean(deal.nextActivityDue) && Date.parse(deal.nextActivityDue!) < now
  );
}

export function companyDealRow(deal: DealListItem, t: UseTranslationResult['t']): EntityRelatedRow {
  const overdue = isDealNextContactOverdue(deal);
  // Просроченную дату уже показывает чип «Просрочен с …» — в мета-строке не дублируем.
  const nextContactDue = overdue ? null : deal.nextActivityDue;
  const contactName = deal.contact
    ? formatPersonName(deal.contact.firstName, deal.contact.lastName)
    : null;
  return {
    id: deal.id,
    title: deal.title,
    href: `/deals/${deal.id}`,
    status: (
      <span className="company-row-status">
        <EntityStatusChip
          tone={deal.stage === 'Won' ? 'success' : deal.stage === 'Lost' ? 'error' : 'processing'}
        >
          {getEnumLabel('dealStage', deal.stage, t)}
        </EntityStatusChip>
        {overdue && deal.nextActivityDue ? (
          <EntityStatusChip tone="error">
            {t('crm.deal_detail.step_overdue_since', {
              date: formatDateTime(deal.nextActivityDue).slice(0, 10),
            })}
          </EntityStatusChip>
        ) : null}
      </span>
    ),
    meta:
      deal.contact || nextContactDue ? (
        <>
          {deal.contact ? <Link to={`/contacts/${deal.contact.id}`}>{contactName}</Link> : null}
          {deal.contact && nextContactDue ? ' · ' : null}
          {nextContactDue ? (
            <time dateTime={nextContactDue}>
              {t('crm.features.company_detail.next_contact_at', {
                date: formatDateTime(nextContactDue),
              })}
            </time>
          ) : null}
        </>
      ) : null,
    value: <span style={MONO_NUMERIC_STYLE}>{formatMoney(deal.amount, deal.currency)}</span>,
  };
}

export function CompanyDealsTab({
  companyId,
  deals,
  loading,
  error,
}: {
  companyId: string;
  deals: DealListItem[];
  loading: boolean;
  error: Error | null;
}): ReactElement {
  const { t, locale } = useTranslation();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('next_contact');
  const [direction, setDirection] = useState<'ascend' | 'descend'>('ascend');
  const [create, setCreate] = useState(false);
  const due = (deal: DealListItem): number =>
    deal.nextActivityDue ? Date.parse(deal.nextActivityDue) : Infinity;
  const sorted = [...deals].sort((a, b) => {
    if (sort === 'original') return 0;
    let order = 0;
    if (sort === 'amount') order = Number(a.amount) - Number(b.amount);
    if (sort === 'stage')
      order =
        STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage) ||
        a.stage.localeCompare(b.stage, locale);
    if (sort === 'next_contact') {
      const left = due(a);
      const right = due(b);
      if (left !== right) order = left === Infinity ? 1 : right === Infinity ? -1 : left - right;
    }
    order ||= a.title.localeCompare(b.title, locale);
    return direction === 'ascend' ? order : -order;
  });
  const needle = search.trim().toLocaleLowerCase();
  return (
    <>
      <EntityRelatedList
        className="contact-deals-list"
        loading={loading}
        error={error}
        emptyText={t(
          'crm.features.features.companies.pages.companydetailpage.у_компании_и_ее_контактов_пока_нет_сделок',
        )}
        filter={
          <div className="contact-deals-filter company-detail-related-sort">
            <Input.Search
              aria-label={t('crm.entity_detail.search')}
              placeholder={t('crm.entity_detail.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Select
              size="small"
              aria-label={t('crm.entity_detail.sort')}
              value={sort}
              onChange={(value) => {
                setSort(value);
                setDirection(value === 'amount' ? 'descend' : 'ascend');
              }}
              style={{ minWidth: 200 }}
              popupMatchSelectWidth={false}
              options={[
                {
                  value: 'next_contact',
                  label: t('crm.features.company_detail.sort_next_contact'),
                },
                { value: 'amount', label: t('crm.entity_detail.sort_amount') },
                { value: 'stage', label: t('crm.entity_detail.sort_stage') },
                { value: 'title', label: t('crm.entity_detail.sort_title') },
                { value: 'original', label: t('crm.features.company_detail.sort_original') },
              ]}
            />
            <Select
              size="small"
              aria-label={t('crm.features.company_detail.sort_direction')}
              value={direction}
              onChange={setDirection}
              disabled={sort === 'original'}
              style={{ minWidth: 160 }}
              options={['ascend', 'descend'].map((value) => ({
                value,
                label: t(`crm.features.company_detail.sort_${value}`),
              }))}
            />
          </div>
        }
        action={
          <Button size="small" icon={<PlusOutlined />} onClick={() => setCreate(true)}>
            {t('crm.entity_detail.add_deal')}
          </Button>
        }
        rows={sorted
          .filter((deal) => !needle || deal.title.toLocaleLowerCase().includes(needle))
          .map((deal) => companyDealRow(deal, t))}
      />
      {create ? (
        <DealFormModal open lockedCompanyId={companyId} onClose={() => setCreate(false)} />
      ) : null}
    </>
  );
}
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
